import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { getResources } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { CatalogModal } from "@/components/CatalogModal";

export const Library = () => {
  const { t, tr } = useLanguage();
  const [catalogOpen, setCatalogOpen] = useState(false);
  const { data: resources = [], isLoading } = useQuery({ queryKey: ["resources"], queryFn: getResources });

  return (
    <section id="library" className="py-20 md:py-28" data-testid="library">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="max-w-2xl mb-10">
          <Reveal>
            <span className="font-sans uppercase tracking-[0.25em] text-xs font-semibold text-primary">
              {t("library.overline")}
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-serif text-4xl sm:text-6xl tracking-tight text-foreground">
              {t("library.title")}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 font-sans text-base md:text-lg leading-relaxed font-light text-muted-foreground">
              {t("library.note")}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <button
              type="button"
              onClick={() => setCatalogOpen(true)}
              data-testid="library-open-catalog"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-primary px-8 py-4 font-sans uppercase tracking-[0.18em] text-xs font-semibold text-primary-foreground shadow-md shadow-primary/25 hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-300 group"
            >
              {t("library.viewCatalogCta")}
              <ArrowRight size={15} strokeWidth={2} className="group-hover:translate-x-1.5 transition-transform duration-300" />
            </button>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 font-sans text-sm leading-relaxed text-primary/90 border-l-2 border-primary/40 pl-4">
              {t("library.intro")}
            </p>
          </Reveal>
        </div>

        <CatalogModal open={catalogOpen} onOpenChange={setCatalogOpen} />

        <Reveal delay={0.1}>
          <div
            className="flex gap-5 md:gap-6 overflow-x-auto pb-5 -mx-6 px-6 md:-mx-10 md:px-10 snap-x snap-mandatory [scrollbar-width:thin]"
            data-testid="resources-carousel"
          >
            {isLoading &&
              [...Array(6)].map((_, i) => (
                <div key={i} className="shrink-0 w-56 sm:w-64 aspect-[3/4] rounded-sm animate-pulse bg-foreground/[0.04]" />
              ))}

            {resources.map((r) => {
              const c = tr(r.translations);
              return (
                <div
                  key={r.id}
                  data-testid={`resource-${r.id}`}
                  className="group shrink-0 w-56 sm:w-64 snap-start"
                >
                  <div
                    className="relative overflow-hidden rounded-sm aspect-[3/4] bg-[#efe9dd] ring-1 ring-black/10 cursor-zoom-in"
                    onMouseMove={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = ((e.clientX - rect.left) / rect.width) * 100;
                      const y = ((e.clientY - rect.top) / rect.height) * 100;
                      const img = e.currentTarget.querySelector("img");
                      if (img) {
                        img.style.transformOrigin = `${x}% ${y}%`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      const img = e.currentTarget.querySelector("img");
                      if (img) img.style.transformOrigin = "center center";
                    }}
                  >
                    <img
                      src={r.image}
                      alt={c.title}
                      loading="lazy"
                      className="w-full h-full object-cover object-top transition-transform duration-300 ease-out group-hover:scale-[2.5]"
                    />
                    <span className="absolute top-3 left-3 font-sans text-[10px] uppercase tracking-[0.15em] text-white/95 px-2.5 py-0.5 rounded-full bg-black/50 backdrop-blur-sm pointer-events-none">
                      {r.year}
                    </span>
                  </div>
                  <h3 className="mt-3 font-serif text-lg leading-tight text-foreground group-hover:text-primary transition-colors duration-300">
                    {c.title}
                  </h3>
                  <p className="font-sans text-[10px] uppercase tracking-[0.15em] text-primary/80 mt-1">{r.author}</p>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
